import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { YEAR_FORMAT, YYYY } from "constants/date";
import { Field, FieldProps } from "formik";
import { useToast } from "hooks/useToast";
import { useUpdateTaskAssigneeAdminMutation } from "store/apis/serviceTeam";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";

type ChangeTaskSeasonProps = {
  isOpen: boolean;
  close: () => void;
  taskDeadline: TaskDeadlineOverviewItem;
};

type ChangeTaskSeasonValues = {
  season: string;
};

const validationSchema = object({
  season: string().required("Season is required"),
});

export const ChangeTaskSeason = ({
  isOpen,
  close,
  taskDeadline,
}: ChangeTaskSeasonProps) => {
  const { alertToast, successToast } = useToast();
  const [updateTaskAssigneeAdmin, { isLoading }] =
    useUpdateTaskAssigneeAdminMutation();

  const initialValues: ChangeTaskSeasonValues = {
    season: String(taskDeadline.season ?? ""),
  };

  const handleSubmit = async ({ season }: ChangeTaskSeasonValues) => {
    try {
      await updateTaskAssigneeAdmin({
        taskId: taskDeadline.task_uuid!,
        season,
      }).unwrap();
      successToast({ message: "Season updated successfully" });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small">
        <FormikForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Modal.Header>
            <Modal.Title>Change season</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <Field name="season">
              {({ field }: FieldProps) => (
                <DateInput
                  {...field}
                  label="Season"
                  required
                  showYearPicker
                  dateFormat={YEAR_FORMAT}
                  saveFormat={YYYY}
                  placeholder="YYYY"
                  portalId="change-task-season"
                  block
                />
              )}
            </Field>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button">Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Save
            </Button>
          </Modal.FooterButtonGroup>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
